import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const WIDTH = 256;

/**
 * A button that opens a small menu of actions beside it (role="menu", items are role="menuitem"
 * buttons). `align="end"` lines the menu up with the button's end edge, for buttons at the end of a row.
 * `items` gets `close`, so an item can close the menu before it acts.
 */
export function Menu({ label, className, title, align = "start", trigger, items }: {
  label: string; className: string; title?: string; align?: "start" | "end"; trigger: ReactNode; items: (close: () => void) => ReactNode;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const close = () => { setPosition(null); button.current?.focus(); };
  const show = () => {
    if (position) { close(); return; }
    const rect = button.current!.getBoundingClientRect();
    const rtl = getComputedStyle(button.current!).direction === "rtl";
    const atEnd = (align === "end") !== rtl;
    setPosition({ top: rect.bottom + 6, left: Math.max(8, Math.min(atEnd ? rect.right - WIDTH : rect.left, window.innerWidth - WIDTH - 8)) });
  };
  // A menu that would leave the window opens above its button instead.
  useLayoutEffect(() => {
    if (!position || !menu.current || !button.current) return;
    const height = menu.current.offsetHeight;
    if (position.top + height <= window.innerHeight - 8) return;
    const above = button.current.getBoundingClientRect().top - 6 - height;
    const top = Math.max(8, above);
    if (top !== position.top) setPosition({ ...position, top });
  }, [position]);
  useEffect(() => {
    if (!position) return;
    if (!menu.current?.contains(document.activeElement)) menu.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    const outside = (event: PointerEvent) => { if (!menu.current?.contains(event.target as Node) && !button.current?.contains(event.target as Node)) setPosition(null); };
    const dismiss = () => setPosition(null);
    document.addEventListener("pointerdown", outside);
    document.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("scroll", dismiss, true); window.removeEventListener("resize", dismiss); };
  }, [position]);
  return <>
    <button ref={button} className={className} aria-label={label} title={title} aria-haspopup="menu" aria-expanded={!!position} onClick={show} onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); if (!position) show(); } }}>{trigger}</button>
    {position && createPortal(<div ref={menu} className="person-menu" role="menu" aria-label={label} style={{ ...position, width: WIDTH }} onKeyDown={(event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); }
      if (event.key === "Tab") setPosition(null);
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const options = Array.from(menu.current!.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
        const next = (options.indexOf(document.activeElement as HTMLButtonElement) + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
        options[next]?.focus();
      }
    }}>{items(close)}</div>, document.body)}
  </>;
}
